import React from 'react';
import { ArrowDownIcon, ArrowRightIcon, FileTextIcon } from 'lucide-react';
import { SmartLink } from '../components/SmartLink';
import { StatsRow } from '../components/company/StatsRow';
import { CardGrid } from '../components/company/CardGrid';
import { SectionShell } from '../components/company/SectionShell';
import { PlaceholderNote } from '../components/company/PlaceholderNote';
import { CtaBand } from '../components/company/CtaBand';
import { JobList } from '../components/careers/JobList';
import { scrollToAnchor } from '../utils/scroll';
import { benefits, careerValues, careersImages, careersStats, hiringSteps, jobs, lifePhotos, whyKayko } from '../data/careers';
export function Careers() {
  return <main data-id="element-1893" data-mp="35enar6ais1xe">
      <section className="hero-surface w-full" aria-labelledby="careers-heading" data-id="element-1894" data-mp="35ff1o3t69bhc">
        <div className="mx-auto max-w-shell px-6 pt-16 lg:pt-24" data-id="element-1895" data-mp="3agckmim0a9jw">
          <div className="max-w-[840px]" data-id="element-1896" data-mp="3fdl5iqyjgrou">
            <p className="text-[14px] font-semibold text-brand" data-id="element-1897" data-mp="3femfst7qda5i">Careers at Kayko</p>
            <h1 id="careers-heading" className="display mt-4 text-[38px] font-semibold text-ink sm:text-[50px] lg:text-[60px]" data-id="element-1898" data-mp="1jmuhs6h6hyy4">
              Help build the financial backbone for Rwanda’s small businesses
            </h1>
            <p className="mt-6 max-w-[600px] text-[18px] leading-[1.5] text-ink-soft" data-id="element-1899" data-mp="1almuskk44kjc">
              Join a team giving thousands of SMEs the tools to grow.
            </p>
            <button type="button" onClick={() => scrollToAnchor('open-roles')} className="mt-8 inline-flex h-12 items-center gap-2 rounded-[7px] bg-brand px-6 text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover" data-id="element-1900" data-mp="22bhoetf06wdt">
              See open roles
              <ArrowDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-1901" data-mp="1ojbgfum07vpg" />
            </button>
          </div>
          <img src={careersImages.hero} alt="The Kayko team talking around a table in the Kigali office" className="mt-12 h-[260px] w-full rounded-t-[14px] object-cover sm:h-[380px] lg:mt-16 lg:h-[500px]" data-id="element-1902" data-mp="2m2cstgedu61v" />
        </div>
      </section>

      <CardGrid id="why-kayko-heading" title="Why Kayko" subtitle="Work that matters to the businesses around you." items={whyKayko} columns={4} data-id="element-1903" data-mp="3579alesyim8f" />

      <StatsRow stats={careersStats} label="Kayko in numbers" data-id="element-1904" data-mp="2vdisqsrc5a1p" />

      <SectionShell id="life-heading" title="Life at Kayko" data-id="element-1905" data-mp="2hej0zc8j0f1b">
        <ul className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 lg:grid-rows-2" data-id="element-1906" data-mp="2menklb4qdpvr">
          {lifePhotos.map((photo, i) => <li key={photo.src} className={i === 0 ? 'col-span-2 lg:row-span-2' : ''} data-id="element-1907" data-mp="28fnrnra6qots" data-mpi={i}>
              <img src={photo.src} alt={photo.alt} loading="lazy" className={`w-full rounded-[12px] object-cover ${i === 0 ? 'h-[240px] sm:h-[320px] lg:h-full' : 'h-[160px] sm:h-[220px]'}`} data-id="element-1908" data-mp="2cfy33jgqy7g1" />
            </li>)}
        </ul>
        <p className="mt-5 max-w-[640px] text-[15px] leading-[1.6] text-ink-muted" data-id="element-1909" data-mp="1v6djjlxyr3bn">
          We spend our weeks building in the office, visiting customers in their shops, and a few days a year together outside the city.
        </p>
      </SectionShell>

      <SectionShell id="career-values-heading" title="Our values" tone="warm" data-id="element-1910" data-mp="24yt5svgame2c">
        <ul className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1911" data-mp="204xay1vbt7be">
          {careerValues.map(({
          icon: Icon,
          title,
          text
        }) => <li key={title} data-id="element-1912" data-mp="29wkuzhpjzm2u">
              <Icon className="h-6 w-6 text-brand" strokeWidth={1.75} aria-hidden="true" data-id="element-1913" data-mp="29v9r7hw3yebq" />
              <h3 className="mt-4 text-[18px] font-semibold tracking-[-0.02em] text-ink" data-id="element-1914" data-mp="2nurka9gfsdqw">{title}</h3>
              <p className="mt-2 text-[15px] leading-[1.55] text-ink-soft" data-id="element-1915" data-mp="2ixsuu47pwuz1">{text}</p>
            </li>)}
        </ul>
      </SectionShell>

      <CardGrid id="benefits-heading" title="Benefits" subtitle="What you can expect when you join." items={benefits} columns={4} note={<PlaceholderNote data-id="element-1917" data-mp="qx3r8abrrid7">All benefits are marked [to confirm] until HR finalises the package.</PlaceholderNote>} data-id="element-1916" data-mp="tjmlbigaophw" />

      <SectionShell id="hiring-heading" title="How we hire" subtitle="Four steps, usually within three weeks." tone="warm" data-id="element-1918" data-mp="v0tduu8vvpvl">
        <ol className="grid grid-cols-1 gap-8 md:grid-cols-4 md:gap-6" data-id="element-1919" data-mp="14wwdntg9jcfd">
          {hiringSteps.map((step, i) => <li key={step.title} className="relative flex gap-4 md:block" data-id="element-1920" data-mp="7a5sb661p1qk" data-mpi={i}>
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink text-[15px] font-semibold text-white" data-id="element-1921" data-mp="c87l4it9wff8">
                {i + 1}
              </span>
              {i < hiringSteps.length - 1 && <span className="absolute left-[52px] right-0 top-5 hidden h-px bg-ink/15 md:block" aria-hidden="true" data-id="element-1922" data-mp="lzbwz9nnjk5u" />}
              <div className="md:mt-5" data-id="element-1923" data-mp="3km6fjg0ljsqm">
                <h3 className="text-[18px] font-semibold text-ink" data-id="element-1924" data-mp="3pmu8i78dyjnh">{step.title}</h3>
                <p className="mt-1.5 text-[15px] leading-[1.55] text-ink-soft" data-id="element-1925" data-mp="3i3kws7ceq8v9">{step.text}</p>
              </div>
            </li>)}
        </ol>
      </SectionShell>

      <section id="open-roles" className="w-full scroll-mt-[96px] bg-white py-20" aria-labelledby="open-roles-heading" data-id="element-1926" data-mp="3d2ow02ncr2u2">
        <div className="mx-auto max-w-shell px-6" data-id="element-1927" data-mp="336u5ymgsu5h9">
          <h2 id="open-roles-heading" className="display text-[32px] font-semibold text-ink sm:text-[44px]" data-id="element-1928" data-mp="33628t77jzzov">
            Open roles
          </h2>
          <div className="mt-8" data-id="element-1929" data-mp="2ueju70q99rxw">
            <JobList jobs={jobs} data-id="element-1930" data-mp="2ud8ouwh5wc4o" />
          </div>

          <div className="mt-12 flex flex-col gap-4 rounded-[14px] bg-canvas-warm px-6 py-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-1931" data-mp="2ucfjnzxupuon">
            <p className="flex items-start gap-3" data-id="element-1932" data-mp="2kh5ej3c5p76n">
              <FileTextIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-1933" data-mp="2ke9hdh84umj9" />
              <span data-id="element-1934" data-mp="2fiu4oslv2wof">
                <span className="block text-[16px] font-semibold text-ink" data-id="element-1935" data-mp="2bfo2px5s7rnr">Don’t see your role?</span>
                <span className="mt-1 block text-[14px] text-ink-soft" data-id="element-1936" data-mp="20ukan15ap0k6">
                  We’re always glad to hear from talented people. Tell us how you’d help.
                </span>
              </span>
            </p>
            <SmartLink href="/contact" className="group inline-flex shrink-0 items-center gap-1.5 pl-8 text-[15px] font-semibold text-brand transition-colors duration-150 ease-out hover:text-brand-hover sm:pl-0" data-id="element-1937" data-mp="1rudz3kt5qann">
              Send us your CV
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-0.5" aria-hidden="true" data-id="element-1938" data-mp="2okelxe9yuh2b" />
            </SmartLink>
          </div>
        </div>
      </section>

      <CtaBand headline="Build what’s next for Rwanda’s businesses" subheadline="Learn more about who we are, or get in touch with the team." primary={{
      label: 'About Kayko',
      href: '/about'
    }} secondary={{
      label: 'Contact us',
      href: '/contact'
    }} data-id="element-1939" data-mp="2fqrt9x121txc" />
    </main>;
}